import {
  DEFAULT_RESPONSE_MINUTES,
  LEAD_OPERATIONS,
  WEEKDAYS,
  type BusinessHoursInput,
  type LeadOperation,
  type Weekday,
} from '@erp/shared';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowDown, ArrowUp, Pencil, Plus, Trash2, X } from 'lucide-react';
import { useState } from 'react';

import { Alert } from '../../components/ui/alert.tsx';
import { Badge } from '../../components/ui/badge.tsx';
import { Button } from '../../components/ui/button.tsx';
import { Card } from '../../components/ui/card.tsx';
import { Dialog } from '../../components/ui/dialog.tsx';
import { Field, Input, Select } from '../../components/ui/field.tsx';
import { Loading } from '../../components/ui/skeleton.tsx';
import { useMembers, type Member } from '../../lib/admin-api.ts';
import { api, errorMessage } from '../../lib/api.ts';
import { useChannels } from '../../lib/contacts-api.ts';
import { useToast } from '../../lib/toast.tsx';

interface LeadSettings {
  leadManagerMembershipId: string | null;
  businessHours: BusinessHoursInput;
}

interface RoutingRule {
  id: string;
  name: string;
  channel: { id: string; name: string } | null;
  operation: LeadOperation | null;
  responseMinutes: number;
  active: boolean;
  position: number;
  members: { membershipId: string; name: string; active: boolean }[];
}

const SETTINGS_KEY = ['settings', 'leads'] as const;
const RULES_KEY = ['lead-routing-rules'] as const;

/** HU-CRM-03: who handles each lead from the website or Meta, and when it moves on. */
export function LeadsSettingsPage() {
  const settings = useQuery({
    queryKey: SETTINGS_KEY,
    queryFn: () => api<LeadSettings>('/settings/leads'),
  });
  const rules = useQuery({
    queryKey: RULES_KEY,
    queryFn: () => api<RoutingRule[]>('/lead-routing-rules'),
  });
  const members = useMembers(false);
  const error = settings.error ?? rules.error ?? members.error;
  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">Leads</h1>
        <p className="text-sm text-muted-foreground">
          Quién atiende los leads del sitio web y de Meta. Si el contacto ya tiene agente, el lead
          va a ese agente. Si no, la primera regla activa que coincide lo asigna en rotación; si el
          agente no responde a tiempo (una nota, una actividad o un cambio en el negocio), pasa al
          siguiente, y tras una vuelta completa, al encargado de leads.
        </p>
      </div>
      {error ? (
        <Alert tone="danger">{errorMessage(error)}</Alert>
      ) : !settings.data || !rules.data || !members.data ? (
        <Loading />
      ) : (
        <>
          <SettingsCard
            settings={settings.data}
            members={members.data.filter((m) => m.status === 'active')}
          />
          <RulesCard
            rules={rules.data}
            members={members.data.filter((m) => m.status === 'active')}
          />
        </>
      )}
    </div>
  );
}

function SettingsCard({ settings, members }: { settings: LeadSettings; members: Member[] }) {
  const client = useQueryClient();
  const notify = useToast();
  const [manager, setManager] = useState(settings.leadManagerMembershipId ?? '');
  const [hours, setHours] = useState<BusinessHoursInput>(settings.businessHours);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      client.setQueryData(
        SETTINGS_KEY,
        await api<LeadSettings>('/settings/leads', {
          method: 'PATCH',
          body: { leadManagerMembershipId: manager || null, businessHours: hours },
        }),
      );
      notify('Ajustes de leads guardados.', 'success');
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };
  const setDay = (day: Weekday, value: [string, string] | undefined) => {
    setHours(
      Object.fromEntries(
        (Object.keys(WEEKDAYS) as Weekday[]).flatMap((d) => {
          const v = d === day ? value : hours[d];
          return v ? [[d, v]] : [];
        }),
      ),
    );
  };
  return (
    <Card className="space-y-4">
      <form
        className="space-y-5"
        onSubmit={(e) => {
          e.preventDefault();
          void save();
        }}
      >
        <Field
          label="Encargado de leads"
          hint="Recibe los leads que ninguna regla asigna y los que nadie respondió a tiempo."
        >
          {(a11y) => (
            <Select
              {...a11y}
              value={manager}
              onChange={(e) => {
                setManager(e.target.value);
              }}
            >
              <option value="">El administrador más antiguo</option>
              {members.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.displayName}
                </option>
              ))}
            </Select>
          )}
        </Field>
        <fieldset className="space-y-2">
          <legend className="text-sm font-medium">Horario de atención</legend>
          <p className="text-sm text-muted-foreground">
            El tiempo para responder solo corre en este horario (y no en feriados).
          </p>
          {(Object.keys(WEEKDAYS) as Weekday[]).map((day) => {
            const value = hours[day];
            return (
              <div key={day} className="flex flex-wrap items-center gap-2">
                <label className="flex min-h-11 w-32 items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    className="size-5"
                    checked={Boolean(value)}
                    onChange={(e) => {
                      setDay(day, e.target.checked ? ['08:00', '17:00'] : undefined);
                    }}
                  />
                  {WEEKDAYS[day]}
                </label>
                {value ? (
                  <>
                    <Input
                      type="time"
                      aria-label={`${WEEKDAYS[day]}: abre`}
                      className="w-32"
                      value={value[0]}
                      onChange={(e) => {
                        setDay(day, [e.target.value, value[1]]);
                      }}
                    />
                    <span className="text-sm text-muted-foreground">a</span>
                    <Input
                      type="time"
                      aria-label={`${WEEKDAYS[day]}: cierra`}
                      className="w-32"
                      value={value[1]}
                      onChange={(e) => {
                        setDay(day, [value[0], e.target.value]);
                      }}
                    />
                  </>
                ) : (
                  <span className="text-sm text-muted-foreground">Cerrado</span>
                )}
              </div>
            );
          })}
        </fieldset>
        {error && <Alert tone="danger">{error}</Alert>}
        <Button type="submit" variant="secondary" loading={busy}>
          Guardar
        </Button>
      </form>
    </Card>
  );
}

function RulesCard({ rules, members }: { rules: RoutingRule[]; members: Member[] }) {
  const client = useQueryClient();
  const notify = useToast();
  const [editing, setEditing] = useState<RoutingRule | 'new' | null>(null);
  const [deleting, setDeleting] = useState<RoutingRule | null>(null);
  const move = async (index: number, delta: number) => {
    const ids = rules.map((r) => r.id);
    const [id] = ids.splice(index, 1);
    if (!id) return;
    ids.splice(index + delta, 0, id);
    try {
      client.setQueryData(
        RULES_KEY,
        await api<RoutingRule[]>('/lead-routing-rules/order', { method: 'PUT', body: { ids } }),
      );
    } catch (err) {
      notify(errorMessage(err), 'error');
    }
  };
  return (
    <Card className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h2 className="font-semibold">Reglas de asignación</h2>
          <p className="text-sm text-muted-foreground">
            Se revisan en este orden; la primera que coincide asigna el lead.
          </p>
        </div>
        <Button
          variant="secondary"
          onClick={() => {
            setEditing('new');
          }}
        >
          <Plus aria-hidden /> Nueva regla
        </Button>
      </div>
      {rules.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          Sin reglas: todos los leads van al encargado de leads.
        </p>
      ) : (
        <ol className="divide-y divide-border">
          {rules.map((rule, index) => (
            <li key={rule.id} className="flex flex-wrap items-start gap-3 py-3">
              <div className="min-w-0 flex-1 space-y-1">
                <p className="font-medium">
                  {index + 1}. {rule.name} {!rule.active && <Badge tone="warning">Inactiva</Badge>}
                </p>
                <p className="text-sm text-muted-foreground">
                  {rule.channel?.name ?? 'Cualquier canal'} ·{' '}
                  {rule.operation ? LEAD_OPERATIONS[rule.operation] : 'Cualquier operación'} ·{' '}
                  {rule.responseMinutes} min para responder
                </p>
                <p className="text-sm">
                  {rule.members.map((m, i) => (
                    <span key={m.membershipId} className={m.active ? '' : 'line-through'}>
                      {i > 0 ? ' → ' : ''}
                      {m.name}
                    </span>
                  ))}
                </p>
              </div>
              <div className="flex gap-1">
                <Button
                  size="icon"
                  variant="ghost"
                  aria-label={`Subir ${rule.name}`}
                  disabled={index === 0}
                  onClick={() => void move(index, -1)}
                >
                  <ArrowUp aria-hidden />
                </Button>
                <Button
                  size="icon"
                  variant="ghost"
                  aria-label={`Bajar ${rule.name}`}
                  disabled={index === rules.length - 1}
                  onClick={() => void move(index, 1)}
                >
                  <ArrowDown aria-hidden />
                </Button>
                <Button
                  size="icon"
                  variant="ghost"
                  aria-label={`Editar ${rule.name}`}
                  onClick={() => {
                    setEditing(rule);
                  }}
                >
                  <Pencil aria-hidden />
                </Button>
                <Button
                  size="icon"
                  variant="ghost"
                  aria-label={`Borrar ${rule.name}`}
                  onClick={() => {
                    setDeleting(rule);
                  }}
                >
                  <Trash2 aria-hidden />
                </Button>
              </div>
            </li>
          ))}
        </ol>
      )}
      {editing && (
        <RuleDialog
          rule={editing === 'new' ? null : editing}
          members={members}
          onClose={() => {
            setEditing(null);
          }}
        />
      )}
      {deleting && (
        <DeleteDialog
          rule={deleting}
          onClose={() => {
            setDeleting(null);
          }}
        />
      )}
    </Card>
  );
}

function RuleDialog({
  rule,
  members,
  onClose,
}: {
  rule: RoutingRule | null;
  members: Member[];
  onClose: () => void;
}) {
  const client = useQueryClient();
  const notify = useToast();
  const channels = useChannels();
  const [name, setName] = useState(rule?.name ?? '');
  const [channelId, setChannelId] = useState(rule?.channel?.id ?? '');
  const [operation, setOperation] = useState<string>(rule?.operation ?? '');
  const [minutes, setMinutes] = useState(String(rule?.responseMinutes ?? DEFAULT_RESPONSE_MINUTES));
  const [active, setActive] = useState(rule?.active ?? true);
  const [chosen, setChosen] = useState<{ membershipId: string; name: string }[]>(
    rule?.members.map(({ membershipId, name: n }) => ({ membershipId, name: n })) ?? [],
  );
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const available = members.filter((m) => !chosen.some((c) => c.membershipId === m.id));

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      await api(rule ? `/lead-routing-rules/${rule.id}` : '/lead-routing-rules', {
        method: rule ? 'PATCH' : 'POST',
        body: {
          name,
          channelId: channelId || null,
          operation: operation || null,
          responseMinutes: minutes,
          active,
          membershipIds: chosen.map((c) => c.membershipId),
        },
      });
      await client.invalidateQueries({ queryKey: RULES_KEY });
      notify(rule ? 'Regla guardada.' : 'Regla creada.', 'success');
      onClose();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };
  const swap = (index: number, delta: number) => {
    const next = [...chosen];
    const [item] = next.splice(index, 1);
    if (item) next.splice(index + delta, 0, item);
    setChosen(next);
  };

  return (
    <Dialog open onClose={onClose} title={rule ? 'Editar regla' : 'Nueva regla'}>
      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          void save();
        }}
      >
        <Field label="Nombre" hint="Por ejemplo: «Sitio web: compras».">
          {(a11y) => (
            <Input
              {...a11y}
              required
              maxLength={80}
              value={name}
              onChange={(e) => {
                setName(e.target.value);
              }}
            />
          )}
        </Field>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Canal">
            {(a11y) => (
              <Select
                {...a11y}
                value={channelId}
                onChange={(e) => {
                  setChannelId(e.target.value);
                }}
              >
                <option value="">Cualquier canal</option>
                {(channels.data ?? [])
                  .filter((c) => c.active || c.id === channelId)
                  .map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
              </Select>
            )}
          </Field>
          <Field label="Operación">
            {(a11y) => (
              <Select
                {...a11y}
                value={operation}
                onChange={(e) => {
                  setOperation(e.target.value);
                }}
              >
                <option value="">Cualquier operación</option>
                {Object.entries(LEAD_OPERATIONS).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </Select>
            )}
          </Field>
        </div>
        <Field
          label="Minutos para responder"
          hint="Minutos dentro del horario de atención. Si no responde, pasa al siguiente."
        >
          {(a11y) => (
            <Input
              {...a11y}
              type="number"
              inputMode="numeric"
              min={1}
              max={1440}
              required
              value={minutes}
              onChange={(e) => {
                setMinutes(e.target.value);
              }}
            />
          )}
        </Field>
        <fieldset className="space-y-2">
          <legend className="text-sm font-medium">Agentes, en el orden de la rotación</legend>
          {chosen.length === 0 && (
            <p className="text-sm text-muted-foreground">Elige al menos un agente.</p>
          )}
          <ol className="space-y-1">
            {chosen.map((c, index) => (
              <li key={c.membershipId} className="flex items-center gap-1">
                <span className="flex-1 text-sm">
                  {index + 1}. {c.name}
                </span>
                <Button
                  type="button"
                  size="icon"
                  variant="ghost"
                  aria-label={`Subir a ${c.name}`}
                  disabled={index === 0}
                  onClick={() => {
                    swap(index, -1);
                  }}
                >
                  <ArrowUp aria-hidden />
                </Button>
                <Button
                  type="button"
                  size="icon"
                  variant="ghost"
                  aria-label={`Bajar a ${c.name}`}
                  disabled={index === chosen.length - 1}
                  onClick={() => {
                    swap(index, 1);
                  }}
                >
                  <ArrowDown aria-hidden />
                </Button>
                <Button
                  type="button"
                  size="icon"
                  variant="ghost"
                  aria-label={`Quitar a ${c.name}`}
                  onClick={() => {
                    setChosen(chosen.filter((x) => x.membershipId !== c.membershipId));
                  }}
                >
                  <X aria-hidden />
                </Button>
              </li>
            ))}
          </ol>
          {available.length > 0 && (
            <Field label="Agregar agente">
              {(a11y) => (
                <Select
                  {...a11y}
                  value=""
                  onChange={(e) => {
                    const member = members.find((m) => m.id === e.target.value);
                    if (member) {
                      setChosen([...chosen, { membershipId: member.id, name: member.displayName }]);
                    }
                  }}
                >
                  <option value="">Elegir…</option>
                  {available.map((m) => (
                    <option key={m.id} value={m.id}>
                      {m.displayName}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
          )}
        </fieldset>
        <label className="flex min-h-11 items-center gap-3 text-sm">
          <input
            type="checkbox"
            className="size-5"
            checked={active}
            onChange={(e) => {
              setActive(e.target.checked);
            }}
          />
          Activa
        </label>
        {error && <Alert tone="danger">{error}</Alert>}
        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
          <Button type="submit" loading={busy}>
            Guardar
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

function DeleteDialog({ rule, onClose }: { rule: RoutingRule; onClose: () => void }) {
  const client = useQueryClient();
  const notify = useToast();
  const [busy, setBusy] = useState(false);
  const remove = async () => {
    setBusy(true);
    try {
      await api(`/lead-routing-rules/${rule.id}`, { method: 'DELETE' });
      await client.invalidateQueries({ queryKey: RULES_KEY });
      notify('Regla borrada.', 'success');
      onClose();
    } catch (err) {
      notify(errorMessage(err), 'error');
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog
      open
      onClose={onClose}
      title="Borrar regla"
      description={`«${rule.name}» deja de asignar leads. Los leads que esperan respuesta pasan al encargado si nadie responde.`}
    >
      <div className="flex justify-end gap-2">
        <Button variant="ghost" onClick={onClose}>
          Cancelar
        </Button>
        <Button variant="danger" loading={busy} onClick={() => void remove()}>
          Borrar
        </Button>
      </div>
    </Dialog>
  );
}
